import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Trash2, X, Search, Users, DoorOpen } from 'lucide-react';
import RupiahIcon from '@/components/RupiahIcon';
import axios from 'axios';
import { isFinanceAccount, currentAccount } from '@/lib/accountPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyManagement = ({ userRole = 'admin', account }) => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProperty, setSelectedProperty] = useState(null);
  
  // Check if user is superadmin
  const isSuperAdmin = userRole === 'superadmin';
  const canDeleteProperty = isSuperAdmin && !isFinanceAccount(account || currentAccount());
  
  // Search & Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  
  const [formData, setFormData] = useState({
    owner_email: '',
    name: '',
    location: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [propertiesRes, ownersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/properties`, config),
        axios.get(`${BACKEND_URL}/api/admin/owners`, config)
      ]);
      
      setProperties(propertiesRes.data);
      setFilteredProperties(propertiesRes.data);
      setOwners(ownersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  // Filter handler
  useEffect(() => {
    let filtered = properties;

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedOwner && selectedOwner !== 'all') {
      filtered = filtered.filter(p => p.owner_email === selectedOwner);
    }

    setFilteredProperties(filtered);
  }, [searchTerm, selectedOwner, properties]);

  const handlePropertyClick = (property) => {
    navigate(`/admin/finance/${encodeURIComponent(property.name)}/${encodeURIComponent(property.owner_email)}`);
  };

  const handleOccupancyClick = (property) => {
    navigate(`/admin/occupancy/${encodeURIComponent(property.name)}/${encodeURIComponent(property.owner_email)}`);
  };

  const handleRoomsClick = (property) => {
    navigate(`/admin/rooms/${encodeURIComponent(property.name)}/${encodeURIComponent(property.owner_email)}`);
  };

  const handleOpenModal = (mode, property = null) => {
    setModalMode(mode);
    setSelectedProperty(property);
      if (mode === 'edit' && property) {
        setFormData({
          owner_email: property.owner_email,
          name: property.name,
          location: property.location
        });
      } else {
        setFormData({
          owner_email: '',
          name: '',
          location: ''
        });
      }
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      owner_email: '',
      name: '',
      location: ''
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (modalMode === 'create') {
        await axios.post(
          `${BACKEND_URL}/api/admin/properties?owner_email=${formData.owner_email}`,
          {
            name: formData.name,
            location: formData.location,
            occupied_rooms: 0,  // Dihitung otomatis dari check-in/check-out
            monthly_revenue: 0  // Dihitung otomatis dari transaksi
          },
          config
        );
        setSuccess('Properti berhasil ditambahkan!');
      } else {
        await axios.put(
          `${BACKEND_URL}/api/admin/properties/${encodeURIComponent(selectedProperty.name)}?owner_email=${encodeURIComponent(selectedProperty.owner_email)}&new_owner_email=${encodeURIComponent(formData.owner_email)}&new_name=${encodeURIComponent(formData.name)}&location=${encodeURIComponent(formData.location)}`,
          {},
          config
        );
        setSuccess('Properti berhasil diupdate!');
      }

      fetchData();
      setTimeout(() => handleCloseModal(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (property) => {
    if (!canDeleteProperty) return;
    if (!window.confirm(`Hapus properti ${property.name}? Semua transaksi terkait akan ikut terhapus.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(
        `${BACKEND_URL}/api/admin/properties/${property.name}?owner_email=${property.owner_email}`,
        config
      );
      setSuccess('Properti berhasil dihapus!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menghapus properti');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-isk-muted">Memuat data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
          {success}
        </div>
      )}
      {error && !showModal && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold text-isk-text">Kelola Properti</h3>
          <p className="text-isk-muted mt-1">Total {filteredProperties.length} dari {properties.length} properti</p>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={() => handleOpenModal('create')}
            className="bg-isk-teal hover:bg-isk-dark text-white rounded-full px-6 py-3 font-semibold shadow-lg"
            data-testid="add-property-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Tambah Properti
          </Button>
        )}
      </div>

      {/* Search & Filter */}
      <div className="glass-card rounded-2xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-isk-text font-semibold">Cari Properti</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-isk-muted" />
              <Input
                type="text"
                placeholder="Cari nama atau lokasi properti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="glass-input border-2 focus:border-isk-teal rounded-xl pl-10"
                data-testid="search-property"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-isk-text font-semibold">Filter by Owner</Label>
            <Select value={selectedOwner} onValueChange={setSelectedOwner}>
              <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="filter-owner">
                <SelectValue placeholder="Semua Owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Owner</SelectItem>
                {owners.map((owner, idx) => (
                  <SelectItem key={idx} value={owner.email}>
                    {owner.name} ({owner.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Properties Table */}
      <div className="glass-card rounded-2xl p-8">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="properties-table">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-4 px-4 text-sm font-semibold text-isk-text">Nama Properti</th>
                <th className="text-left py-4 px-4 text-sm font-semibold text-isk-text">Owner</th>
                <th className="text-left py-4 px-4 text-sm font-semibold text-isk-text">Lokasi</th>
                <th className="text-center py-4 px-4 text-sm font-semibold text-isk-text">Kamar</th>
                <th className="text-center py-4 px-4 text-sm font-semibold text-isk-text">Okupansi</th>
                {isSuperAdmin && <th className="text-right py-4 px-4 text-sm font-semibold text-isk-text">Pendapatan/Bulan</th>}
                <th className="text-center py-4 px-4 text-sm font-semibold text-isk-text">Kelola Kamar</th>
                <th className="text-center py-4 px-4 text-sm font-semibold text-isk-text">Check-in/out</th>
                {isSuperAdmin && (
                  <th className="text-center py-4 px-4 text-sm font-semibold text-isk-text">Aksi</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredProperties.map((property, index) => (
                <tr 
                  key={index} 
                  className="border-b border-white/10 hover:bg-white/30 transition-colors cursor-pointer"
                  onClick={() => handlePropertyClick(property)}
                  data-testid={`property-row-${index}`}
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-lg bg-isk-teal/10 flex items-center justify-center mr-3">
                        <RupiahIcon className="w-5 h-5 text-isk-teal" />
                      </div>
                      <div>
                        <p className="text-sm text-isk-text font-medium">{property.name}</p>
                        <p className="text-xs text-isk-muted">Klik untuk kelola finance</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-sm text-isk-muted">{property.owner_email}</td>
                  <td className="py-4 px-4 text-sm text-isk-muted">{property.location}</td>
                  <td className="py-4 px-4 text-sm text-center text-isk-text">
                    {property.occupied_rooms}/{property.total_rooms}
                  </td>
                  <td className="py-4 px-4 text-sm text-center">
                    <span className="px-3 py-1 rounded-full bg-isk-teal/10 text-isk-teal font-semibold">
                      {property.occupancy_rate}%
                    </span>
                  </td>
                  {isSuperAdmin && (
                    <td className="py-4 px-4 text-sm text-right font-semibold text-isk-text">
                      {formatCurrency(property.monthly_revenue)}
                    </td>
                  )}
                  <td className="py-4 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <Button
                      onClick={() => handleRoomsClick(property)}
                      variant="outline"
                      size="sm"
                      className="border-blue-400/50 text-blue-600 hover:bg-blue-600 hover:text-white"
                      data-testid={`rooms-property-${index}`}
                    >
                      <DoorOpen className="w-4 h-4 mr-1" />
                      Kamar
                    </Button>
                  </td>
                  <td className="py-4 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <Button
                      onClick={() => handleOccupancyClick(property)}
                      variant="outline"
                      size="sm"
                      className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white"
                      data-testid={`occupancy-property-${index}`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Kelola
                    </Button>
                  </td>
                  {isSuperAdmin && (
                    <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center space-x-2">
                        <Button
                          onClick={() => handleOpenModal('edit', property)}
                          variant="outline"
                          size="sm"
                          className="border-isk-gold/30 text-isk-gold hover:bg-isk-gold hover:text-white"
                          data-testid={`edit-property-${index}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {canDeleteProperty && <Button
                          onClick={() => handleDelete(property)}
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white"
                          data-testid={`delete-property-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
          <div className="glass-card rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="property-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-isk-text">
                {modalMode === 'create' ? 'Tambah Properti Baru' : 'Edit Properti'}
              </h3>
              <button onClick={handleCloseModal} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="owner">Owner *</Label>
                <Select
                  value={formData.owner_email}
                  onValueChange={(value) => setFormData({ ...formData, owner_email: value })}
                  required
                >
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="modal-select-owner">
                    <SelectValue placeholder="Pilih Owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {owners.map((owner, idx) => (
                      <SelectItem key={idx} value={owner.email}>
                        {owner.name} ({owner.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nama Properti *</Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Kost Melati Kemang"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="modal-input-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Lokasi *</Label>
                <Input
                  id="location"
                  type="text"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g. Kemang, Jakarta Selatan"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="modal-input-location"
                />
              </div>

              {/* Info: Kamar terisi dan pendapatan dihitung otomatis */}
              <div className="p-4 rounded-xl bg-isk-teal/5 border border-isk-teal/20">
                <p className="text-sm text-isk-muted">
                  <strong>Info:</strong> Total kamar, kamar terisi, dan pendapatan bulanan dihitung otomatis berdasarkan data kamar, check-in/check-out, dan transaksi keuangan.
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                  data-testid="modal-submit-button"
                >
                  {modalMode === 'create' ? 'Tambah Properti' : 'Simpan Perubahan'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyManagement;
